/**************************************************/
/* Methods for PLS and PCVPLS based on wasm code  */
/**************************************************/

import { ismatrix, Matrix  } from 'mdatools/arrays';
import { pcares_get } from './pca.js';

import { isArrCorrect, checkedIndexCopy, alloc, allocCopy, checkModelBuffer, wasmError }
   from './utils.js';

/**
 * Returns length of array with PLS results.
 *
 * @param {number} nrows number of rows in a dataset.
 * @param {number} nresp number of response variables.
 * @param {number} ncomp number of components in the model.
 * @param {number} npred number of predictors in the model.
 *
 * @returns {number} the length of the results array.
 */
export function pls_getreslength(nrows, nresp, ncomp, npred) {
   // Structure of PLS results:

      // nr * ncomp +         // scores (T)
      // nr * ncomp +         // score distances (H)
      // nr * ncomp +         // orthogonal distances (Q)
      // ncomp +              // cumulative explained variance (xcumexpvar)
      // nr * ny * ncomp +    // predicted values (Yp)
      // nr * ncomp +         // u-scores (U)
      // nr * ncomp +         // residual distances (Z)
      // ncomp +              // cumulative explained variance (ycumexpvar)
      // ny * ncomp +         // RMSE
      // ny * ncomp +         // R2
      // ny * ncomp +         // bias
      // ny * ncomp;          // slope

   return nrows * ncomp +       // T
      nrows * ncomp +           // H
      nrows * ncomp +           // Q
      ncomp +                   // xcumexpvar
      nrows * nresp * ncomp +   // Yp
      nrows * ncomp +           // U
      nrows * ncomp +           // Z
      ncomp +                   // ycumexpvar
      nresp * ncomp +           // RMSE
      nresp * ncomp +           // R2
      nresp * ncomp +           // bias
      nresp * ncomp +           // slope
      nrows * npred;            // x-residuals
}

/**
 * Returns length of array with PLS model.
 *
 * @param {number} npred number of predictors.
 * @param {number} nresp number of responses.
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function pls_getmodellength(npred, nresp, ncomp) {
   // Structure of PLS model - same as PCA with extra fields at the end:
   // 3 +               // nx, ny and ncomp values
   // 2 * (nx + ny) +   // mean and sd vectors for dataset (x and y together)
   // nx * ncomp +      // x-loadings (P)
   // ncomp +           // x-eigenvalues
   // 4 * ncomp +       // statistics for q-distances
   // 4 * ncomp +       // statistics for h-distances
   // 4 * ncomp +       // statistics for z-distances
   // nx * ncomp +      // weights (R)
   // ny * ncomp +      // y-loadings (C)
   // ncomp;            // y-eigenvalues

   return 3  +                    // nc, ncomp
      2 * (npred + nresp) +       // (mY + mX, sY + sX)
      npred * ncomp +   // P
      1 * ncomp +       // xeigenvals
      4 * ncomp +       // cq0, cNq, rq0, rNq
      4 * ncomp +       // ch0, cNh, rh0, rNh
      4 * ncomp +       // cz0, cNz, rz0, rNz
      npred * ncomp +   // R
      nresp * ncomp +   // C
      1 * ncomp;        // yeigenvals
}


/**
 * Returns part of PLS model array for given outcome.
 *
 * @param {TypedArray} m array with PLS model.
 * @param {string} name name of the outcome (e.g. 'yloadings', 'mY', 'sY', etc.).
 * @param {number} a component number (for loadings).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function plsmodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes("plsmodel")) {
      throw new Error('plsmodel_get: parameter "m" must be JSON with PLS model.');
   }

   const npred = m.npred;
   const nresp = m.nresp;
   const ncomp = m.ncomp;

   let length;
   let offset = pls_getmodellength(npred, nresp, ncomp) - npred * ncomp - nresp * ncomp - 2 * nresp;

   switch (name) {

      case 'mY':
         offset = 3;
         length = nresp;
         break;

      case 'mX':
         offset = 3 + nresp;
         length = npred;
         break;

      case 'sY':
         offset = 3 + nresp + npred;
         length = nresp;
         break;

      case 'sX':
         offset = 3 + nresp + npred + nresp;
         length = npred;
         break;

      case 'P':
         offset = 3 + 2 * (nresp + npred );
         length = ncomp * npred;
         break;

      case 'loadings':
      case 'xloadings':
         offset = 3 + 2 * (nresp + npred ) + (a - 1) * npred;
         length = npred;
         break;

      case 'eigenvals':
      case 'xeigenvals':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred;
         length = ncomp;
         break;

      case 'cq0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + ncomp;
         length = ncomp;
         break;

      case 'cNq':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 2 * ncomp;
         length = ncomp;
         break;

      case 'rq0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 3 * ncomp;
         length = ncomp;
         break;

      case 'rNq':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 4 * ncomp;
         length = ncomp;
         break;

      case 'ch0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 5 * ncomp;
         length = ncomp;
         break;

      case 'cNh':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 6 * ncomp;
         length = ncomp;
         break;

      case 'rh0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 7 * ncomp;
         length = ncomp;
         break;

      case 'rNh':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 8 * ncomp;
         length = ncomp;
         break;

      case 'cz0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 9 * ncomp;
         length = ncomp;
         break;

      case 'cNz':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 10 * ncomp;
         length = ncomp;
         break;

      case 'rz0':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 11 * ncomp;
         length = ncomp;
         break;

      case 'rNz':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 12 * ncomp;
         length = ncomp;
         break;

      case 'weights':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 13 * ncomp + (a - 1) * npred;
         length = npred;
         break;

      case 'yloadings':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 13 * ncomp + ncomp * npred + (a - 1) * nresp;
         length = nresp;
         break;

      case 'yeigenvals':
         offset = 3 + 2 * (nresp + npred ) + ncomp * npred + 13 * ncomp + ncomp * npred + ncomp * nresp;
         length = ncomp;
         break;
      default:
         throw Error('plsmodel_get: wrong outcome name.');
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns part of PLS result array for given outcome.
 *
 * @param {TypedArray} r array with PLS results.
 * @param {string} name name of the outcome (e.g. 'Yp'); 'Ypall' gives the predictions for every
 * component and response at once (nrows x nresp x ncomp) and ignores 'a' and 'ny'.
 * @param {number} a component number (for scores, predictions and distances).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function plsres_get(r, name, a, ny) {

   if (!r || !r.class || !r.class.includes("plsres")) {
      throw new Error('plsres_get: parameter "r" must be JSON with PLS results.');
   }

   if (!ny) {
      ny = 1;
   }


   const nrows = r.nrows;
   const ncomp = r.ncomp;
   const npred = r.npred;
   const nresp_model = r.nresp_model;

   let length;
   let offset = 3 * nrows * ncomp + ncomp; // T, H, Q + xcumexpvar

   switch (name) {
      case 'Ypall':
         length = nrows * ncomp * nresp_model;
         break;
      case 'Yps':
         offset = offset + (a - 1) * nresp_model * nrows;
         length = nrows * nresp_model;
         break;
      case 'Yp':
         offset = offset + (a - 1) * nresp_model * nrows + (ny - 1) * nrows;
         length = nrows;
         break;
      case 'yscores':
         offset = offset + ncomp * nresp_model * nrows + (a - 1) * nrows;
         length = nrows;
         break;
      case 'zdist':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + (a - 1) * nrows;
         length = nrows;
         break;
      case 'ycumexpvar':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows;
         length = ncomp;
         break;
      case 'rmse':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows + ncomp;
         length = nresp_model * ncomp;
         break;
      case 'r2':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows + ncomp + nresp_model * ncomp;
         length = nresp_model * ncomp;
         break;
      case 'bias':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows + ncomp + 2 * nresp_model * ncomp;
         length = nresp_model * ncomp;
         break;
      case 'slope':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows + ncomp + 3 * nresp_model * ncomp;
         length = nresp_model * ncomp;
         break;
      case 'E':
         offset = offset + ncomp * nresp_model * nrows + ncomp * nrows + ncomp * nrows + ncomp + 4 * nresp_model * ncomp;
         length = nrows * npred;
         break;
      case 'T':
         offset = 0;
         length = nrows * ncomp;
         break;

      default:
         return pcares_get(r, name, a);
   }

   return r.v.subarray(offset, offset + length);
}




/**
 * Creates a PLS model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Matrix} D matrix with training set (predictors and responses).
 * @param {number} nresp number of responses.
 * @param {number} ncomp number of components.
 * @param {boolean} center mean center the data columns or not.
 * @param {boolean|number} scale false/0 - no scaling, true/1 - scale X and Y, 2 - scale X only
 * (Y not scaled, PLS-DA).
 * @param {Array} outliers array with indices of outliers.
 * @param {Array} exclvars array with indices of excluded variables.
 *
 * @returns {Object} JSON with PLS model.
 */
export function pls_fit(Module, D, nresp, ncomp, center, scale, outliers, exclvars) {

   if (!ismatrix(D)) {
      throw new Error("pls_fit: D must be a matrix.");
   }

   if (nresp < 1 || D.ncols < (2 + nresp)) {
      throw new Error("pls_fit: training set should have at least 2 predictors and 1 response.");
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, D.nrows - 1)) {
      throw new Error('pls_fit: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pls_fit: indices in "outliers" array must be whole numbers.');

   // process excluded variables
   if (!exclvars) exclvars = [];
   const nexv = exclvars && Array.isArray(exclvars) ? exclvars.length : 0;
   if (!isArrCorrect(exclvars, 0, D.ncols - nresp - 1)) {
      throw new Error('pls_fit: some indices in "exclvars" array are outside the expected range.');
   }

   // the same truncation as for the outlier indices above: a variable index that is not a whole
   // number reaches C through 'Int32Array' as a different column, so the model would be fitted
   // without a variable the caller never named. a separate name for the same reason as above -
   // the model returned below carries the caller's own 'exclvars' array
   const exclvarValues = checkedIndexCopy(exclvars,
      'pls_fit: indices in "exclvars" array must be whole numbers.');

   const nrows = D.nrows - nout;
   const npred = D.ncols - nresp - nexv;

   if (nrows < 3 || npred < 2) {
      throw new Error('pls_fit: X must have at least 3 rows and 2 predictors.');
   }

   // set the default values
   if (center === undefined) {
      center = true;
   }

   if (scale === undefined) {
      scale = false;
   }

   if (ncomp === undefined) {
      ncomp = Math.min(nrows - 1, npred);
   }


   if (ncomp < 1 || ncomp > Math.min(nrows - 1, npred)) {
      throw new Error(
         'pls_fit: the value of "ncomp" does not match the dimension of D.',
      );
   }

   // pointers and the result copies are hoisted so the 'finally' below can free every allocation no
   // matter where a throw happens, and so the copies outlive the block. a pointer which was never
   // assigned stays 0, and '_free(0)' is a no-op, so the frees below need no guards
   const m_length = pls_getmodellength(npred, nresp, ncomp);
   const m_constructor = Float64Array;
   const vip_length = npred * nresp * ncomp;
   const vip_constructor = Float64Array;
   let d_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, vip_ptr = 0, coeffs_ptr = 0, sr_ptr = 0;
   let model, coeffs, vip, sr;

   try {

      // allocate memory in WASM heap space and copy data values
      d_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(exclvarValues), 'excluded variables');
      }

      // allocate memory in WASM heap space for model and calibration set results outcomes
      m_ptr = alloc(Module, m_length * m_constructor.BYTES_PER_ELEMENT, 'model');

      // allocate memory in WASM heap space for regression coefficients. these three are sized with
      // 'vip_constructor', the same constructor their slices below are read back with - the sizing
      // used to name 'm_constructor' instead, which happened to be the same type
      vip_ptr = alloc(Module, vip_length * vip_constructor.BYTES_PER_ELEMENT, 'VIP');
      coeffs_ptr = alloc(Module, vip_length * vip_constructor.BYTES_PER_ELEMENT, 'regression coefficients');
      sr_ptr = alloc(Module, vip_length * vip_constructor.BYTES_PER_ELEMENT, 'selectivity ratio');

      // call WASM function for PLS
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'plsfit_wasm',
         [ 'number'], // return type
         [ 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
         [d_ptr, D.nrows, D.ncols - nresp, nresp, ncomp, center, scale, m_ptr, o_ptr, nout, ev_ptr, nexv, coeffs_ptr, vip_ptr, sr_ptr],
      );

      if (res !== 1) {
         throw wasmError(Module, 'pls_fit', mark);
      }

      // copy part of HEAP memory with model outcomes. these have to stay 'slice' - each makes an
      // independent buffer, while a 'subarray' would be a view into memory the 'finally' frees
      model = Module.HEAPF64.slice(
         m_ptr / m_constructor.BYTES_PER_ELEMENT,
         m_ptr / m_constructor.BYTES_PER_ELEMENT + m_length,
      );

      // copy part of HEAP memory with coeffs, vip and sr
      coeffs = Module.HEAPF64.slice(
         coeffs_ptr / vip_constructor.BYTES_PER_ELEMENT,
         coeffs_ptr / vip_constructor.BYTES_PER_ELEMENT + vip_length,
      );

      vip = Module.HEAPF64.slice(
         vip_ptr / vip_constructor.BYTES_PER_ELEMENT,
         vip_ptr / vip_constructor.BYTES_PER_ELEMENT + vip_length,
      );

      sr = Module.HEAPF64.slice(
         sr_ptr / vip_constructor.BYTES_PER_ELEMENT,
         sr_ptr / vip_constructor.BYTES_PER_ELEMENT + vip_length,
      );

   } finally {

      // freeing after a wasm trap is expected to be safe: the instance survives an ordinary trap
      // and every pointer above is either 0 or valid. a trap caused by earlier heap corruption
      // could still leave the allocator damaged, and a throw from here would then mask the original
      // error - accepted rather than guarded, since this build exports no abort flag
      Module._free(d_ptr);
      Module._free(m_ptr);
      Module._free(coeffs_ptr);
      Module._free(vip_ptr);
      Module._free(sr_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return PLS model object (can be also treated as PCA model)
   return {
      class: ['plsmodel', 'pcamodel'],
      v: model,
      ncomp: ncomp,
      nrows: nrows,
      npred: npred,
      nresp: nresp,
      exclvars: exclvars,
      center: center,
      scale: scale,
      coeffs: coeffs,
      vip: vip,
      sr: sr
   };
}


/**
 * Projects dataset to a PLS model.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Object} m JSON with PLS model created by `pls_fit()`.
 * @param {Matrix} D matrix with dataset.
 * @param {Number} nresp number of responses in D.
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Object} JSON with PLS results.
 */
export function pls_predict(Module, m, D, nresp, outliers) {

   if (!m || !m.class || !m.class.includes('plsmodel')) {
      throw new Error('pls_predict: parameter "m" must be JSON with PLS results.');
   }

   // the header order is [nresp, npred, ncomp] while 'pls_getmodellength' takes
   // (npred, nresp, ncomp) - the first two are swapped here on purpose
   checkModelBuffer(m, 'pls_predict', [[0, 'nresp'], [1, 'npred'], [2, 'ncomp']],
      (ny, nx, ncomp) => pls_getmodellength(nx, ny, ncomp));

   if (!ismatrix(D)) {
      throw new Error('pls_predict: D must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, D.nrows - 1)) {
      throw new Error('pls_predict: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pls_predict: indices in "outliers" array must be whole numbers.');

   // the model keeps the caller's 'exclvars' array by REFERENCE, and a model read from a file
   // brings its own, so these values have never been through the check in the fit function - the
   // caller can even mutate the array after fitting. the truncation is the same: a column index
   // that is not a whole number reaches C as a different column.
   //
   // the container is tested here because this path has no 'isArrCorrect' to do it. everything
   // computed from these indices reads the dense copy. that is naming discipline, not a second
   // line of defence: the guard above has already refused every list the copy would differ from,
   // and nothing here is async, so re-reading 'm.exclvars' would be harmless. the result hands
   // 'm.exclvars' itself back as metadata, unchanged. see 'checkedIndexCopy'
   if (!Array.isArray(m.exclvars)) {
      throw new Error('pls_predict: indices in "exclvars" taken from the model must be whole numbers.');
   }
   const modelExclvars = checkedIndexCopy(m.exclvars,
      'pls_predict: indices in "exclvars" taken from the model must be whole numbers.');

   // excluded variables are taken from the model
   let nexv = modelExclvars.length;
   let exclvars = modelExclvars;

   // compute numbers of rows and columns without excluded ones
   const nrows = D.nrows - nout;

   // check if dataset alread excluded variables the ecluded variables array will be emptied
   if ((D.ncols - nresp) === m.npred) {
      exclvars = [];
      nexv = 0;
   }

   // compute new number of predictors
   const npred = D.ncols - nexv - nresp;

   if (nrows < 1) {
      throw new Error('pls_predict: D must have at least 1 row.');
   }

   if (npred !== m.npred) {
      throw new Error('pls_predict: D must have the same number of predictors (without excluded ones) as the training set.');
   }

   // see the note in 'pls_fit' - pointers and the copy are hoisted so the 'finally' can free
   // unconditionally and the copy outlives the block
   const r_length = pls_getreslength(nrows, m.nresp, m.ncomp, m.npred);
   const r_constructor = Float64Array;
   let d_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, r_ptr = 0;
   let res_values;

   try {

      // allocate memory in WASM heap space and copy dataset values
      d_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(modelExclvars), 'excluded variables');
      }

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for result values
      r_ptr = alloc(Module, r_length * r_constructor.BYTES_PER_ELEMENT, 'results');

      // call WASM function for PCA prediction
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'plspredict_wasm',
         ['number'], // return type
         ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
         [d_ptr, D.nrows, D.ncols - nresp, nresp, m_ptr, r_ptr, o_ptr, nout, ev_ptr, nexv],
      ); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'pls_predict', mark);
      }

      // copy part of HEAP memory with result values - 'slice', not 'subarray', see 'pls_fit'
      res_values = Module.HEAPF64.slice(
         r_ptr / r_constructor.BYTES_PER_ELEMENT,
         r_ptr / r_constructor.BYTES_PER_ELEMENT + r_length,
      );

   } finally {
      Module._free(d_ptr);
      Module._free(m_ptr);
      Module._free(r_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return PLS results (can be also treated as PCA results)
   const resobj = {
      class: ['plsres', 'pcares'],
      v: res_values,
      ncomp: m.ncomp,
      nrows: nrows,
      npred: npred,
      nresp: nresp,
      nresp_model: m.nresp,
      outliers: outliers,
      exclvars: m.exclvars,
      center: m.center,
      scale: m.scale,
   };

   // compute object with statistics
   const components = new Array(m.ncomp);

   const xexpvar = new Array(m.ncomp);
   const xresvar = new Array(m.ncomp);
   const xcumresvar = new Array(m.ncomp);
   const xcumexpvar = new Array(m.ncomp);

   const yexpvar = new Array(m.ncomp);
   const yresvar = new Array(m.ncomp);
   const ycumresvar = new Array(m.ncomp);
   const ycumexpvar = new Array(m.ncomp);

   const xv = plsres_get(resobj, 'xcumexpvar');
   const yv = plsres_get(resobj, 'ycumexpvar');

   if (nresp > 0) {
      components[0] = 1;

      xcumexpvar[0] = xv[0];
      xcumresvar[0] = 1 - xv[0];
      xexpvar[0] = xv[0];
      xresvar[0] = 1 - xv[0];

      ycumexpvar[0] = yv[0];
      ycumresvar[0] = 1 - yv[0];
      yexpvar[0] = yv[0];
      yresvar[0] = 1 - yv[0];

      for (let i = 1; i < m.ncomp; i++) {
         components[i] = i + 1;

         xcumexpvar[i] = xv[i];
         xcumresvar[i] = 1 - xv[i];
         xexpvar[i] = xv[i] - xv[i - 1];
         xresvar[i] = 1 - xexpvar[i];

         ycumexpvar[i] = yv[i];
         ycumresvar[i] = 1 - yv[i];
         yexpvar[i] = yv[i] - yv[i - 1];
         yresvar[i] = 1 - yexpvar[i];
      }

      const rmse = new Matrix(plsres_get(resobj, "rmse"), m.nresp, m.ncomp);
      const r2 = new Matrix(plsres_get(resobj, "r2"), m.nresp, m.ncomp);
      const slope = new Matrix(plsres_get(resobj, "slope"), m.nresp, m.ncomp);
      const bias = new Matrix(plsres_get(resobj, "bias"), m.nresp, m.ncomp);

      // add statistics and return
      resobj.stat = {
         components,
         xresvar, xexpvar, xcumexpvar, xcumresvar,
         yresvar, yexpvar, ycumexpvar, ycumresvar,

         rmse: rmse.t(),
         r2: r2.t(),
         slope: slope.t(),
         bias: bias.t(),
      };
   } else {
      resobj.stat = null;
   }

   return resobj;
}


/**
 * Create a PV-set based on PLS decomposition.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Object} m JSON with PLS model created by `pcr_fit()`.
 * @param {Matrix} D matrix with training set.
 * @param {number} nresp number of responses in D.
 * @param {number} ncomp number of components for PV-set generation.
 * @param {number} cv_nseg number of segments for cross-validation.
 * @param {number} cv_type type of cross-validation (0: loo, 1: ven, 2: rand).
 * @param {number} cv_scope scope of cross-validation (0: global, 1: local).
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Array} array with two matrices: the generated PV-set and matrix of scalars D.
 */
export function pcvpls(Module, m, D, nresp, ncomp, cv_nseg, cv_type, cv_scope, outliers) {

   if (!m || !m.class || !m.class.includes('plsmodel')) {
      throw new Error('pcvpls: parameter "m" must be JSON with PLS model.');
   }

   // the header order is [nresp, npred, ncomp] while 'pls_getmodellength' takes
   // (npred, nresp, ncomp) - the first two are swapped here on purpose
   checkModelBuffer(m, 'pcvpls', [[0, 'nresp'], [1, 'npred'], [2, 'ncomp']],
      (ny, nx, ncomp) => pls_getmodellength(nx, ny, ncomp));

   if (!ismatrix(D)) {
      throw new Error('pcvpls: D must be a matrix.');
   }

   if (nresp < 1) {
      throw new Error('pcvpls: D must have at least one response.');
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, D.nrows - 1)) {
      throw new Error('pcvpls: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'pcvpls: indices in "outliers" array must be whole numbers.');

   // the model keeps the caller's 'exclvars' array by REFERENCE, and a model read from a file
   // brings its own, so these values have never been through the check in the fit function - the
   // caller can even mutate the array after fitting. the truncation is the same: a column index
   // that is not a whole number reaches C as a different column.
   //
   // the container is tested here because this path has no 'isArrCorrect' to do it, and
   // everything computed from these indices reads the dense copy. that is naming discipline, not
   // a second line of defence: the guard above has already refused every list the copy would
   // differ from, and nothing here is async. see 'checkedIndexCopy'
   if (!Array.isArray(m.exclvars)) {
      throw new Error('pcvpls: indices in "exclvars" taken from the model must be whole numbers.');
   }
   const modelExclvars = checkedIndexCopy(m.exclvars,
      'pcvpls: indices in "exclvars" taken from the model must be whole numbers.');

   // excluded variables are taken from the model
   const nexv = modelExclvars.length;

   // compute numbers of rows and columns without excluded ones
   const nrows = D.nrows - nout;
   const npred = D.ncols - nresp - nexv;


   if (nrows < 3) {
      throw new Error('plspcv: D must have at least 3 rows.');
   }

   if (npred < 2) {
      throw new Error('plspcv: D must have at least 2 predictors.');
   }

   if (npred !== m.npred) {
      throw new Error('plspcv: D must have the same number of predictors (without excluded ones) as the training set.');
   }


   if (!Number.isInteger(cv_type) || ![0, 1, 2].includes(cv_type)) {
      throw new Error('plspcv: parameter "cv_type" must be one of 0 ("loo"), 1 ("ven"), 2 ("rand).');
   }

   if (!Number.isInteger(cv_scope) || ![0, 1].includes(cv_scope)) {
      throw new Error('plspcv: parameter "cv_scope" must be 0 ("global") or 1 ("local").');
   }

   if (cv_type > 0 && (!Number.isInteger(cv_nseg) || cv_nseg < 2)) {
      throw new Error('plspcv: parameter "cv_nseg" must be a number ≥ 2.');
   }

   if (cv_type > 0 && cv_nseg > nrows) {
      throw new Error('plspcv: the value of "cv_nseg" is too large for the given X.');
   }

   if (cv_type === 0) {
      cv_nseg = nrows;
   }

   if (!Number.isInteger(ncomp) || ncomp < 1) {
      throw new Error('plspcv: the value of "ncomp" must be a number >= 1.');
   }

   const max_ncomp = cv_type === 0 ?
      Math.min(nrows - 2, npred, m.ncomp) :
      Math.min(nrows - Math.ceil(nrows / cv_nseg) - 1, npred, m.ncomp);

   if (ncomp > max_ncomp) {
      throw new Error('plspcv: the value of "ncomp" is too large.');
   }

   // see the note in 'pls_fit' - pointers and the copies are hoisted so the 'finally' can free
   // unconditionally and the copies outlive the block
   const dpv_constructor = D.v.constructor;
   const dpv_length = nrows * (nresp + npred);
   const s_length = cv_nseg * ncomp;
   const s_constructor = D.v.constructor;
   let d_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, dpv_ptr = 0, s_ptr = 0;
   let dpv, s;

   try {

      // allocate memory in WASM heap space and copy dataset values
      d_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(modelExclvars), 'excluded variables');
      }

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for Dpv values
      dpv_ptr = alloc(Module, dpv_length * dpv_constructor.BYTES_PER_ELEMENT, 'Dpv');

      // allocate memory in WASM heap space for scalar values
      s_ptr = alloc(Module, s_length * s_constructor.BYTES_PER_ELEMENT, 'scalars');

      // call WASM function for PCVPLS
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall('pcvpls_wasm',
                  ['number'],
                  ['number', 'number', 'number', 'number', 'number', 'number', 'number',
                   'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
                  [d_ptr, D.nrows, D.ncols - nresp, nresp, ncomp, m.center, m.scale,
                  cv_type, cv_nseg, cv_scope, m_ptr, dpv_ptr, s_ptr, o_ptr, nout, ev_ptr, nexv]);

      if (res !== 1) {
         throw wasmError(Module, 'plspcv', mark);
      }

      // copy part of HEAP memory with Dpv values - 'slice', not 'subarray', see 'pls_fit'
      dpv = Module.HEAPF64.slice(
         dpv_ptr / dpv_constructor.BYTES_PER_ELEMENT,
         dpv_ptr / dpv_constructor.BYTES_PER_ELEMENT + dpv_length,
      );

      // copy part of HEAP memory with the scalar values
      s = Module.HEAPF64.slice(
         s_ptr / s_constructor.BYTES_PER_ELEMENT,
         s_ptr / s_constructor.BYTES_PER_ELEMENT + s_length,
      );

   } finally {
      Module._free(d_ptr);
      Module._free(m_ptr);
      Module._free(dpv_ptr);
      Module._free(s_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return Dpv and the scalars
   return [new Matrix(dpv, nrows, (nresp + npred)), new Matrix(s, cv_nseg, ncomp)];
}
